import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Logo } from '../../components/brand/Logo';
import { TextAccent } from '../../components/brand/TextAccent';
import { Button } from '../../components/actions/Button';
import { FormField } from '../../components/forms/FormField';
import { TextField } from '../../components/forms/TextField';
import { Checkbox } from '../../components/forms/Checkbox';
import { isApiError } from '../../lib/api/errors';
import { loginWithCode, loginWithEmailAndPassword, sendAccessCode } from '../../services/authService';
import logoCream from '../../design/assets/logo-cream.svg';

/**
 * Admin sign-in, per the kit's SignInScreen: brown-deep hero with the ghost
 * wordmark on the left, the form on cream on the right.
 *
 * identify (email) → password, or → one-time code (emailed; the code step
 * sends it). There is no sign-up and no `/exists` call — the admin app
 * never creates accounts. The kit's "staff accounts require SSO" footer is
 * dropped: there is no SSO.
 */
type Step = 'identify' | 'password' | 'code';

const emailSchema = z.object({ email: z.string().trim().email("That doesn't look like an email address.") });
const passwordSchema = z.object({ password: z.string().min(1, 'Enter your password to continue.'), stay: z.boolean() });
const codeSchema = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code.'), stay: z.boolean() });

function signInError(err: unknown, step: Step): string {
  if (!isApiError(err)) return "We couldn't reach the server. Check your connection and try again.";
  if (step === 'password' && (err.status === 401 || err.status === 403)) return "That email and password don't match.";
  if (step === 'code' && err.status === 410) return 'That code has expired. Send a new one.';
  if (step === 'code' && (err.status === 401 || err.status === 403))
    return "That code isn't valid. Check it and try again.";
  if (err.status === 404) return 'No staff account uses that email.';
  return err.correlationId ? `${err.userMessage} (ID: ${err.correlationId})` : err.userMessage;
}

export function SignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>('identify');
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);

  const identify = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '' },
  });
  const password = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', stay: false },
  });
  const code = useForm<z.infer<typeof codeSchema>>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '', stay: false },
  });

  const back = () => {
    setStep('identify');
    setCodeSent(false);
    password.reset();
    code.reset();
  };

  const onIdentify = identify.handleSubmit((values) => {
    setEmail(values.email.trim());
    setStep('password');
  });

  const onPassword = password.handleSubmit(async (values) => {
    try {
      await loginWithEmailAndPassword(email, values.password, values.stay);
      onSignedIn();
    } catch (err) {
      password.setError('password', { message: signInError(err, 'password') });
    }
  });

  const requestCode = async () => {
    setSending(true);
    try {
      await sendAccessCode(email);
      setCodeSent(true);
      code.clearErrors();
    } catch (err) {
      code.setError('code', { message: signInError(err, 'code') });
    } finally {
      setSending(false);
    }
  };

  const useCode = identify.handleSubmit(async ({ email: value }) => {
    setEmail(value.trim());
    setStep('code');
  });

  const onCode = code.handleSubmit(async (values) => {
    try {
      await loginWithCode(email, values.code, values.stay);
      onSignedIn();
    } catch (err) {
      code.setError('code', { message: signInError(err, 'code') });
    }
  });

  const linkButton = 'border-0 bg-transparent font-body text-sm text-brown underline';

  return (
    <div className="grid min-h-full grid-cols-1 bg-page font-body lg:grid-cols-[minmax(0,1.05fr)_minmax(420px,.95fr)]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-brown-deep px-12 py-10 text-cream lg:flex">
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center px-6 opacity-[.07]"
          aria-hidden="true"
        >
          <img src={logoCream} alt="" className="h-auto w-[clamp(220px,70%,900px)]" />
        </div>
        <Logo variant="cream" height={26} />
        <div className="relative z-[2] max-w-[520px]">
          <div className="mb-[22px] text-[13px] font-semibold uppercase tracking-[.14em] text-orange">
            Mission Control
          </div>
          <h1 className="m-0 mb-[26px] font-heading text-[clamp(40px,4.4vw,64px)] font-extrabold leading-[1.06] tracking-[-.02em] text-cream">
            Keep the neighborhood
            <br />
            running <TextAccent color="orange">smoothly.</TextAccent>
          </h1>
          <p className="m-0 max-w-[48ch] text-[19px] leading-[1.55] text-[rgba(241,237,225,.75)]">
            Visibility into listings, orders, chefs and platform activity — plus the controls that power the HomePlated
            <sup className="tm">™</sup> app.
          </p>
        </div>
        {/* Keeps the headline vertically centred between the logo and the foot of the panel. */}
        <div aria-hidden="true" />
      </section>

      <section className="flex items-center justify-center px-6 py-10 lg:px-12">
        <div className="w-full max-w-[380px]">
          <div className="mb-8 lg:hidden">
            <Logo variant="brown" height={26} />
          </div>
          <h2 className="m-0 mb-2 font-heading text-[30px] font-bold tracking-[-.02em] text-brown-deep">Sign in</h2>
          <p className="m-0 mb-[26px] text-sm text-muted">
            {step === 'identify' ? (
              'Use your HomePlated staff email.'
            ) : (
              <>
                Signing in as <strong className="font-semibold text-ink">{email}</strong>.
              </>
            )}
          </p>

          {step === 'identify' && (
            <form onSubmit={onIdentify} noValidate className="flex flex-col gap-4">
              <FormField label="Email" htmlFor="si-email" error={identify.formState.errors.email?.message}>
                <TextField
                  id="si-email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@homeplated.com"
                  invalid={!!identify.formState.errors.email}
                  {...identify.register('email')}
                />
              </FormField>
              <Button type="submit" fullWidth>
                Continue
              </Button>
              <div className="mt-0.5 flex justify-center">
                <button type="button" className={linkButton} onClick={useCode}>
                  Use a one-time access code
                </button>
              </div>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={onPassword} noValidate className="flex flex-col gap-4">
              <input type="email" autoComplete="username" value={email} readOnly hidden />
              <FormField label="Password" htmlFor="si-pass" error={password.formState.errors.password?.message}>
                <TextField
                  id="si-pass"
                  type="password"
                  autoComplete="current-password"
                  invalid={!!password.formState.errors.password}
                  {...password.register('password')}
                />
              </FormField>
              <Checkbox label="Keep me signed in" {...password.register('stay')} />
              <Button type="submit" fullWidth loading={password.formState.isSubmitting}>
                Sign in
              </Button>
              <div className="mt-0.5 flex flex-col items-center gap-1.5">
                <button type="button" className={linkButton} onClick={() => setStep('code')}>
                  Use a one-time access code
                </button>
                <button type="button" className={linkButton} onClick={back}>
                  Back
                </button>
              </div>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={onCode} noValidate className="flex flex-col gap-4">
              {codeSent ? (
                <FormField
                  label="Access code"
                  htmlFor="si-code"
                  hint="We emailed you a 6-digit code."
                  error={code.formState.errors.code?.message}
                >
                  <TextField
                    id="si-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    invalid={!!code.formState.errors.code}
                    {...code.register('code')}
                  />
                </FormField>
              ) : (
                code.formState.errors.code && (
                  <p role="alert" className="m-0 text-[13px] text-danger">
                    {code.formState.errors.code.message}
                  </p>
                )
              )}
              {codeSent && <Checkbox label="Keep me signed in" {...code.register('stay')} />}
              {codeSent ? (
                <Button type="submit" fullWidth loading={code.formState.isSubmitting}>
                  Sign in
                </Button>
              ) : (
                <Button type="button" fullWidth onClick={requestCode} loading={sending}>
                  {sending ? 'Sending…' : 'Email me a code'}
                </Button>
              )}
              <div className="mt-0.5 flex flex-col items-center gap-1.5">
                {codeSent && (
                  <button type="button" className={linkButton} onClick={requestCode}>
                    Send a new code
                  </button>
                )}
                <button type="button" className={linkButton} onClick={back}>
                  Back
                </button>
              </div>
            </form>
          )}

          <p className="mt-8 text-center text-xs leading-[1.6] text-muted">
            Admin access is audited. Every data change is written to platform activity.
          </p>
        </div>
      </section>
    </div>
  );
}
